import { useMemo, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import {
  AddIcon,
  Button,
  Box as DarkBox,
  FeedItem,
  GuardTourIcon,
  MapPinIcon,
  TextInput,
} from '@hivewatch/dark'

import { TourPlanI } from '@/features/floor-plan'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import {
  descriptionStyles,
  emptyTextStyles,
  rowIconTileStyles,
  rowListStyles,
  rowTitleStyles,
  titleStyles,
} from './DarkTourPlansPanel.styles'
import { DarkTourPlansSidePanel } from './DarkTourPlansSidePanel'

interface DarkTourPlansListIProps {
  facilityId: string
  tourPlans: TourPlanI[]
  isExpanded: boolean
  onToggleExpansion: () => void
  onTourPlanClick: (id: string) => void
  onCreateTourPlanClick: () => void
  onDeviceItemHoverIn: (deviceIds: string[]) => void
  onDeviceItemHoverOut: () => void
}

export const DarkTourPlansList = ({
  facilityId,
  tourPlans,
  isExpanded,
  onToggleExpansion,
  onTourPlanClick,
  onCreateTourPlanClick,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: DarkTourPlansListIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const isEditMode = useAppSelector(selectEditMode)
  const { shouldEnableCreateTourPlan } = usePermissions()
  const [filter, setFilter] = useState('')

  const filteredTourPlans: TourPlanI[] = useMemo(() => {
    return tourPlans?.filter(
      (plan) =>
        plan?.name?.toLocaleLowerCase()?.includes(filter.toLocaleLowerCase()) ||
        filter === ''
    )
  }, [tourPlans, filter])

  const handleCreateClick = () => {
    onCreateTourPlanClick()
    trackEvent('Button Press: Facility Tour Plans - Create Tour Plan', {
      facility_id: facilityId,
      role: me.roleName,
    })
  }

  const handleTourPlanClick = (plan: TourPlanI) => {
    onTourPlanClick(plan.id)
    trackEvent('Button Press: Tour Plan Details', {
      facility_id: facilityId,
      tour_plan_id: plan.id,
      role: me.roleName,
    })
  }

  const header = (
    <>
      {/* Typography only — no leading icon (ENG-32001 / ENG-31744 precedent). */}
      <DarkBox {...titleStyles(colorScheme)}>
        {t('floors:plan_tours_title')}
      </DarkBox>
      <DarkBox {...descriptionStyles(colorScheme)}>
        {shouldEnableCreateTourPlan
          ? t('floors:plan_tours_desc_create')
          : t('floors:plan_tours_desc')}
      </DarkBox>
      <Button
        colorScheme={colorScheme}
        data-testid='tourplan_list_createButton'
        disabled={!isEditMode || !shouldEnableCreateTourPlan}
        leftIcon={<AddIcon size={12} />}
        onClick={handleCreateClick}
        style={{ width: '100%' }}
        variant='primary'
      >
        {t('floors:plan_tours_create_button')}
      </Button>
    </>
  )

  return (
    <DarkTourPlansSidePanel
      header={header}
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
    >
      <DarkBox pb='16px'>
        <TextInput
          colorScheme={colorScheme}
          data-testid='tourplan_list_filterInput'
          onValueChange={setFilter}
          placeholder={t('floors:plan_tours_filter_placeholder')}
          value={filter}
          width='100%'
        />
      </DarkBox>
      {filteredTourPlans?.length > 0 ? (
        <DarkBox {...rowListStyles}>
          {filteredTourPlans.map((plan) => (
            <DarkBox
              key={plan.id}
              onMouseEnter={() => onDeviceItemHoverIn?.(plan.checkpointIds)}
              onMouseLeave={() => onDeviceItemHoverOut?.()}
              w='full'
            >
              <FeedItem
                align='center'
                ariaLabel={plan.name}
                colorScheme={colorScheme}
                icon={
                  // Tours glyph, per Jack Seaton on the ENG-32001 thread: the
                  // mapping's CheckpointsIcon was a slip — a row referencing a
                  // Guard Tour takes the tours icon, not a checkpoint one.
                  <DarkBox {...rowIconTileStyles(colorScheme)}>
                    <GuardTourIcon size={16} />
                  </DarkBox>
                }
                isCompact
                message={<DarkBox {...rowTitleStyles}>{plan.name}</DarkBox>}
                onClick={() => handleTourPlanClick(plan)}
                timestamp={
                  <DarkBox
                    alignItems='center'
                    as='span'
                    display='inline-flex'
                    gap='4px'
                  >
                    <MapPinIcon colorScheme={colorScheme} size={12} />
                    {plan.checkpointCount}
                  </DarkBox>
                }
              />
            </DarkBox>
          ))}
        </DarkBox>
      ) : (
        <DarkBox {...emptyTextStyles(colorScheme)}>
          {t('common:list_empty')}
        </DarkBox>
      )}
    </DarkTourPlansSidePanel>
  )
}
